import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import PageHeader, { KpiTile } from '../components/common/PageHeader';
import ResponsiveTable from '../components/common/ResponsiveTable';
import { NativeSelect, TextArea } from '../components/common/FormField';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { queryKeys } from '../lib/queryClient';
import { api, errorMessage } from '../services/api';
import { Employee } from '../types';
import { blankToNull, fmtCurrency, fmtDate, todayISO, toNum } from '../utils/format';
import { CODE_PATTERN, compact, focusFirstError, hasErrors, serverFieldErrors, v } from '../utils/validation';

interface FormState {
  employeeCode: string;
  name: string;
  designation: string;
  department: string;
  email: string;
  phone: string;
  joiningDate: string;
  exitDate: string;
  monthlySalary: string;
  monthlyAllowances: string;
  panNumber: string;
  bankName: string;
  bankAccountNo: string;
  bankIfsc: string;
  notes: string;
  status: 'active' | 'inactive';
}

const emptyForm = (): FormState => ({
  employeeCode: '', name: '', designation: '', department: '', email: '', phone: '', joiningDate: todayISO(),
  exitDate: '', monthlySalary: '', monthlyAllowances: '', panNumber: '', bankName: '', bankAccountNo: '',
  bankIfsc: '', notes: '', status: 'active',
});

export default function EmployeesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('employees:manage');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const { data: employees = [], isLoading } = useQuery({
    queryKey: queryKeys.employees.all(),
    queryFn: () => api.get<Employee[]>('/employees'),
  });

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      editing ? api.patch(`/employees/${editing.id}`, payload) : api.post('/employees', payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.employees.all() }),
  });

  const active = employees.filter((e) => e.status === 'active');

  function open(e?: Employee) {
    setEditing(e ?? null);
    setErrors({});
    setForm(
      e
        ? {
            employeeCode: e.employeeCode, name: e.name, designation: e.designation ?? '', department: e.department ?? '',
            email: e.email ?? '', phone: e.phone ?? '', joiningDate: e.joiningDate, exitDate: e.exitDate ?? '',
            monthlySalary: String(e.monthlySalary), monthlyAllowances: e.monthlyAllowances ? String(e.monthlyAllowances) : '',
            panNumber: e.panNumber ?? '', bankName: e.bankName ?? '', bankAccountNo: e.bankAccountNo ?? '',
            bankIfsc: e.bankIfsc ?? '', notes: e.notes ?? '', status: e.status,
          }
        : emptyForm(),
    );
    setShowModal(true);
  }

  function setField(key: keyof FormState, value: string) {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => (p[key] ? { ...p, [key]: undefined } : p));
  }

  function validate(): boolean {
    const code = form.employeeCode.trim().toUpperCase();
    const email = form.email.trim().toLowerCase();
    const others = employees.filter((x) => x.id !== editing?.id);
    const e = compact({
      name: v.personName(form.name, 'Full name', true),
      employeeCode:
        v.text(form.employeeCode, 'Employee code', { min: 2, max: 20, pattern: CODE_PATTERN, patternMessage: 'Employee code can only contain letters, digits and -' }) ??
        (code && others.some((x) => x.employeeCode === code) ? 'This employee code is already used' : undefined),
      designation: v.label(form.designation, 'Designation'),
      department: v.label(form.department, 'Department'),
      email: v.email(form.email) ?? (email && others.some((x) => x.email?.toLowerCase() === email) ? 'This email is already used by another employee' : undefined),
      phone: v.phone(form.phone),
      joiningDate: v.date(form.joiningDate, 'Joining date'),
      exitDate: v.date(form.exitDate, 'Exit date', { required: false, min: form.joiningDate || undefined, minLabel: 'the joining date' }),
      monthlySalary: v.money(form.monthlySalary, 'Monthly salary', { allowZero: true }),
      monthlyAllowances: v.money(form.monthlyAllowances, 'Monthly allowances', { required: false, allowZero: true }),
      panNumber: v.pan(form.panNumber),
      bankName: v.businessName(form.bankName, 'Bank name'),
      bankAccountNo: v.bankAccount(form.bankAccountNo) ?? (!form.bankAccountNo && form.bankIfsc ? 'Enter the account number for this IFSC' : undefined),
      bankIfsc: v.ifsc(form.bankIfsc) ?? (!form.bankIfsc && form.bankAccountNo ? 'Enter the IFSC for this account' : undefined),
      notes: v.text(form.notes, 'Notes', { max: 1000 }),
    });
    setErrors(e);
    if (hasErrors(e)) focusFirstError();
    return !hasErrors(e);
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    const { status, monthlySalary, monthlyAllowances, employeeCode, ...rest } = form;
    const payload: Record<string, unknown> = {
      ...blankToNull(rest),
      monthlySalary: toNum(monthlySalary) ?? 0,
      monthlyAllowances: toNum(monthlyAllowances) ?? 0,
    };
    if (employeeCode.trim()) payload.employeeCode = employeeCode.trim();
    if (editing) payload.status = status;
    try {
      await saveMutation.mutateAsync(payload);
      showToast(editing ? 'Employee updated' : 'Employee added', 'success');
      setShowModal(false);
    } catch (err) {
      setErrors(serverFieldErrors(err, Object.keys(form) as (keyof FormState)[]));
      showToast(errorMessage(err, 'Failed to save employee'), 'error');
    }
  }

  const columns = [
    {
      key: 'name',
      label: 'Employee',
      render: (_: any, r: Employee) => (
        <div>
          <span className="text-sm font-semibold text-ink-900">{r.name}</span>
          <span className="block text-xs font-mono text-ink-400">{r.employeeCode}</span>
        </div>
      ),
    },
    {
      key: 'designation',
      label: 'Role',
      render: (_: any, r: Employee) => (
        <div className="text-sm text-ink-700">
          {r.designation ?? '—'}
          {r.department && <span className="block text-xs text-ink-500">{r.department}</span>}
        </div>
      ),
    },
    { key: 'joiningDate', label: 'Joined', render: (_: any, r: Employee) => <span className="text-sm text-ink-600">{fmtDate(r.joiningDate)}</span> },
    { key: 'monthlySalary', label: 'Salary / mo', render: (_: any, r: Employee) => <span className="text-sm font-semibold text-ink-900">{fmtCurrency(r.monthlySalary, 0)}</span> },
    { key: 'monthlyAllowances', label: 'Allowances / mo', render: (_: any, r: Employee) => <span className="text-sm text-ink-700">{r.monthlyAllowances ? fmtCurrency(r.monthlyAllowances, 0) : '—'}</span> },
    {
      key: 'status',
      label: 'Status',
      render: (_: any, r: Employee) =>
        r.status === 'active' ? <Badge variant="success" size="sm" rounded>Active</Badge> : <Badge variant="secondary" size="sm" rounded>Inactive</Badge>,
    },
    ...(canManage
      ? [{
          key: 'actions',
          label: 'Actions',
          width: '100px',
          render: (_: any, r: Employee) => (
            <button type="button" className="px-3 py-1 bg-ink-100 text-ink-700 text-xs rounded hover:bg-ink-200" onClick={() => open(r)}>
              <i className="fas fa-pen mr-1" />Edit
            </button>
          ),
        }]
      : []),
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title="Employees"
          subtitle="Team members and their monthly salary and fixed allowances"
          actions={
            <>
              <Button variant="outline" icon="fa-money-check-dollar" onClick={() => navigate('/payroll')}>Run Payroll</Button>
              {canManage && <Button variant="primary" icon="fa-plus" onClick={() => open()}>Add Employee</Button>}
            </>
          }
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 lg:mb-6">
          <KpiTile label="Active Employees" value={active.length} icon="fa-users" tone="brand" />
          <KpiTile label="Monthly Salary Bill" value={fmtCurrency(active.reduce((s, e) => s + e.monthlySalary, 0), 0)} icon="fa-user-tie" tone="red" />
          <KpiTile label="Monthly Allowances" value={fmtCurrency(active.reduce((s, e) => s + e.monthlyAllowances, 0), 0)} icon="fa-hand-holding-dollar" tone="accent" />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-ink-200 overflow-visible lg:overflow-hidden">
          <div className="px-1 py-3 lg:p-0">
            <ResponsiveTable columns={columns} data={employees} isLoading={isLoading} height="calc(100vh - 340px)" emptyMessage="No employees yet" />
          </div>
        </div>

        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editing ? `Edit Employee — ${editing.employeeCode}` : 'Add Employee'}
          size="lg"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleSubmit as any} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Full Name" required value={form.name} maxLength={120}
                onChange={(e) => setField('name', e.target.value.replace(/[^A-Za-z .'-]/g, ''))} error={errors.name} />
              <Input label="Employee Code" value={form.employeeCode} maxLength={20} placeholder="Auto (ASG-001)" error={errors.employeeCode}
                onChange={(e) => setField('employeeCode', e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                helperText="Leave blank to auto-generate" />
              <Input label="Designation" value={form.designation} maxLength={100} error={errors.designation}
                onChange={(e) => setField('designation', e.target.value)} />
              <Input label="Department" value={form.department} maxLength={100} error={errors.department}
                onChange={(e) => setField('department', e.target.value)} />
              <Input label="Email" type="email" value={form.email} maxLength={255}
                onChange={(e) => setField('email', e.target.value.trim())} error={errors.email} />
              <Input label="Phone" type="tel" value={form.phone} maxLength={14}
                onChange={(e) => setField('phone', e.target.value.replace(/[^0-9+\s-]/g, ''))} error={errors.phone} />
              <Input label="Joining Date" type="date" required value={form.joiningDate}
                onChange={(e) => setField('joiningDate', e.target.value)} error={errors.joiningDate} />
              <Input label="Exit Date" type="date" value={form.exitDate} min={form.joiningDate}
                onChange={(e) => setField('exitDate', e.target.value)} error={errors.exitDate} />
              <Input label="Monthly Salary (₹)" required type="number" value={form.monthlySalary}
                onChange={(e) => setField('monthlySalary', e.target.value)} error={errors.monthlySalary}
                helperText="Net amount paid out each month" />
              <Input label="Monthly Allowances (₹)" type="number" value={form.monthlyAllowances}
                onChange={(e) => setField('monthlyAllowances', e.target.value)} error={errors.monthlyAllowances}
                helperText="Fixed travel / food / phone allowances" />
            </div>

            <p className="text-xs font-bold uppercase tracking-wider text-ink-500 pt-2">Bank &amp; Tax (optional)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="PAN" value={form.panNumber} maxLength={10}
                onChange={(e) => setField('panNumber', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} error={errors.panNumber} />
              <Input label="Bank Name" value={form.bankName} maxLength={255} error={errors.bankName}
                onChange={(e) => setField('bankName', e.target.value)} />
              <Input label="Account Number" value={form.bankAccountNo} maxLength={18}
                onChange={(e) => setField('bankAccountNo', e.target.value.replace(/\D/g, ''))} error={errors.bankAccountNo}
                helperText="9-18 digits" />
              <Input label="IFSC" value={form.bankIfsc} maxLength={11}
                onChange={(e) => setField('bankIfsc', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} error={errors.bankIfsc} />
            </div>
            {editing && (
              <NativeSelect label="Status" value={form.status} onChange={(val) => setField('status', val)}
                options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
            )}
            <TextArea label="Notes" value={form.notes} onChange={(val) => setField('notes', val)} maxLength={1000} error={errors.notes} />
          </form>
        </Modal>
      </div>
    </Layout>
  );
}
